import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiArrowRight, FiCheck, FiX } from 'react-icons/fi';
import '../../css/modal-pasos.css';

/**
 * Asistente por pasos dentro de un modal.
 *
 * Para formularios largos: en vez de una tira de campos con scroll, cada paso
 * pide unas pocas cosas y no se avanza hasta que están bien. El indicador de
 * arriba dice cuántos pasos quedan, que es lo que un formulario largo nunca
 * deja claro.
 *
 * @param pasos [{ clave, titulo, descripcion, contenido, validar }]
 *              `validar` devuelve un texto de error para quedarse en el paso,
 *              o nada para dejar pasar.
 * @param onFinalizar  se llama al terminar el último paso; puede ser asíncrono
 */
const ModalPasos = ({
  abierto, titulo, pasos, onCerrar, onFinalizar,
  textoFinalizar, procesando = false, anchoMaximo = 640,
}) => {
  const { t } = useTranslation();
  const [indice, setIndice] = useState(0);
  const [error, setError] = useState(null);
  const refCuerpo = useRef(null);
  const refTarjeta = useRef(null);

  const paso = pasos[indice];
  const esUltimo = indice === pasos.length - 1;

  // Cada vez que se abre, se empieza de cero
  useEffect(() => {
    if (abierto) {
      setIndice(0);
      setError(null);
    }
  }, [abierto]);

  // Al cambiar de paso, el foco entra en el primer campo y el cuerpo vuelve
  // arriba: si no, se aterriza a media página sin saber dónde
  useEffect(() => {
    if (!abierto) return;
    refCuerpo.current?.scrollTo({ top: 0 });
    const primero = refCuerpo.current?.querySelector('input:not([type="file"]), select, textarea');
    primero?.focus();
  }, [abierto, indice]);

  const cerrar = useCallback(() => {
    if (!procesando) onCerrar();
  }, [procesando, onCerrar]);

  useEffect(() => {
    if (!abierto) return undefined;
    const alTeclear = (e) => { if (e.key === 'Escape') cerrar(); };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierto, cerrar]);

  if (!abierto) return null;

  const avanzar = async () => {
    const fallo = paso.validar?.();
    if (fallo) {
      setError(fallo);
      return;
    }
    setError(null);
    if (!esUltimo) {
      setIndice((i) => i + 1);
      return;
    }
    // Si el guardado falla, el modal se queda abierto con el motivo: cerrarlo
    // daría por hecho un cambio que no llegó a aplicarse
    try {
      await onFinalizar();
    } catch (fallo) {
      setError(fallo?.message || t('pasos.errorGuardar'));
    }
  };

  const retroceder = () => {
    setError(null);
    setIndice((i) => Math.max(0, i - 1));
  };

  // Solo se puede saltar hacia atrás: hacia delante hay que validar
  const irAPaso = (destino) => {
    if (destino >= indice) return;
    setError(null);
    setIndice(destino);
  };

  return createPortal(
    <div className="mp-fondo" onMouseDown={(e) => { if (e.target.classList.contains('mp-fondo')) cerrar(); }}>
      <div
        ref={refTarjeta}
        className="mp-tarjeta"
        style={{ maxWidth: anchoMaximo }}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <div className="mp-cabecera">
          <div>
            <h3>{titulo}</h3>
            <p className="mp-contador">{t('pasos.contador', { actual: indice + 1, total: pasos.length })}</p>
          </div>
          <button type="button" className="mp-cerrar" onClick={cerrar} aria-label={t('comun.cerrar')}>
            <FiX />
          </button>
        </div>

        <ol className="mp-indicador">
          {pasos.map((p, i) => (
            <li key={p.clave} className={`mp-paso ${i === indice ? 'actual' : ''} ${i < indice ? 'hecho' : ''}`}>
              <button
                type="button"
                onClick={() => irAPaso(i)}
                disabled={i >= indice || procesando}
                aria-current={i === indice ? 'step' : undefined}
              >
                <span className="mp-paso-numero">{i < indice ? <FiCheck /> : i + 1}</span>
                <span className="mp-paso-titulo">{p.titulo}</span>
              </button>
            </li>
          ))}
        </ol>

        <div className="mp-cuerpo" ref={refCuerpo}>
          {paso.descripcion && <p className="mp-descripcion">{paso.descripcion}</p>}
          {paso.contenido}
        </div>

        {error && <p className="mp-error" role="alert">{error}</p>}

        <div className="mp-pie">
          {indice > 0 && (
            <button type="button" className="popup-btn popup-cancel mp-atras" onClick={retroceder} disabled={procesando}>
              <FiArrowLeft /> {t('pasos.atras')}
            </button>
          )}
          <button type="button" className="popup-btn popup-cancel" onClick={cerrar} disabled={procesando}>
            {t('comun.cancelar')}
          </button>
          <button type="button" className="popup-btn popup-confirm" onClick={avanzar} disabled={procesando}>
            {esUltimo
              ? (procesando ? t('comun.guardando') : (textoFinalizar || t('comun.guardar')))
              : <>{t('pasos.siguiente')} <FiArrowRight /></>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ModalPasos;
